import {
  RichTextEditor,
  Link,
  useRichTextEditorContext,
} from "@mantine/tiptap";
import { useEditor } from "@tiptap/react";
import Highlight from "@tiptap/extension-highlight";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Superscript from "@tiptap/extension-superscript";
import SubScript from "@tiptap/extension-subscript";
import { forwardRef, memo, useImperativeHandle } from "react";
import { Flex } from "@mantine/core";

interface RichTextProps {
  content?: string;
  handleDataUpdate?: (childValue: string) => void;
  isDisabled?: boolean;
  minHeight?: string;
  keywords?: string[];
  type?: "EMAIL" | "SMS";
}
interface MergeFieldProps {
  keywords?: string[];
}

const MergeField: React.FC<MergeFieldProps> = ({ keywords }) => {
  const { editor } = useRichTextEditorContext();
  if (!keywords || keywords.length === 0) {
    return null; // Return null if keywords are not present or empty
  }
  return (
    <>
      {keywords?.map((trigger) => (
        <RichTextEditor.Control
          key={trigger}
          onClick={() => editor?.commands.insertContent(`{{${trigger}}}`)}
          aria-label="Insert star emoji"
          sx={{ minWidth: "auto", border: 0, padding: 4, fontWeight: 500 }}
        >
          {trigger}
        </RichTextEditor.Control>
      ))}
    </>
  );
};
export interface IGetHTML {
  getHTML(): void;
}

const RichText = forwardRef<IGetHTML, RichTextProps>(
  ({ content = "", isDisabled = false, minHeight, keywords, type }, ref) => {
    const editor = useEditor({
      extensions: [
        StarterKit,
        Underline,
        Link,
        Superscript,
        SubScript,
        Highlight,
        TextAlign.configure({ types: ["heading", "paragraph"] }),
      ],
      content,
    });

    useImperativeHandle(ref, () => ({
      getHTML() {
        return editor?.getHTML() || "";
      },
    }));

    return (
      <RichTextEditor
        editor={editor}
        styles={(theme) => ({
          root: {
            pointerEvents: isDisabled ? "none" : "initial",
            borderColor: "#DDD",
          },
          toolbar: {
            position: "initial",
            display: type === "SMS" ? "none" : "flex",
          },
          content: {
            minHeight: minHeight || "25vh",
            div: {
              minHeight: minHeight || "25vh",
            },
          },
          controlsGroup: {
            button: {
              borderColor: "#DDD",
            },
            "button[data-active]": {
              background: theme.colors.gray[4],
            },
          },
        })}
      >
        <Flex
          wrap={"wrap"}
          gap={5}
          mb={"lg"}
          sx={{ height: "100%", padding: 4, margin: 5 }}
          bg={"white"}
          style={{
            borderRadius: "5px",
          }}
        >
          <MergeField keywords={keywords} />
        </Flex>

        <RichTextEditor.Toolbar sticky stickyOffset={60}>
          <RichTextEditor.ControlsGroup>
            <RichTextEditor.Bold />
            <RichTextEditor.Italic />
            <RichTextEditor.Underline />
            <RichTextEditor.Strikethrough />
            <RichTextEditor.ClearFormatting />
            <RichTextEditor.Highlight />
            <RichTextEditor.Code />
          </RichTextEditor.ControlsGroup>

          <RichTextEditor.ControlsGroup>
            <RichTextEditor.H1 />
            <RichTextEditor.H2 />
            <RichTextEditor.H3 />
            <RichTextEditor.H4 />
          </RichTextEditor.ControlsGroup>

          <RichTextEditor.ControlsGroup>
            <RichTextEditor.Blockquote />
            <RichTextEditor.Hr />
            <RichTextEditor.BulletList />
            <RichTextEditor.OrderedList />
            <RichTextEditor.Subscript />
            <RichTextEditor.Superscript />
          </RichTextEditor.ControlsGroup>

          <RichTextEditor.ControlsGroup>
            <RichTextEditor.Link />
            <RichTextEditor.Unlink />
          </RichTextEditor.ControlsGroup>

          <RichTextEditor.ControlsGroup>
            <RichTextEditor.AlignLeft />
            <RichTextEditor.AlignCenter />
            <RichTextEditor.AlignJustify />
            <RichTextEditor.AlignRight />
          </RichTextEditor.ControlsGroup>
        </RichTextEditor.Toolbar>

        <RichTextEditor.Content />
      </RichTextEditor>
    );
  }
);

export default memo(RichText);
